import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, StepList, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Opladning af VW ID.3 — bespoke EV-model charging guide.
 * Specs from Volkswagen official ID.3 data:
 * - Battery options ~58 kWh (Pro) and ~77 kWh (Pro S) usable.
 * - AC onboard charger up to 11 kW (Type 2); DC fast charge up to ~120–170 kW.
 * - WLTP consumption ~0.15–0.17 kWh/km.
 * No competitor electricity pricing used.
 */
export default function OpladningVwId3({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Hvor hurtigt lader en VW ID.3 hjemme?',
      answer: 'VW ID.3 har en indbygget AC-lader på op til 11 kW. Med en 3-faset ladeboks lader den derfor med op til 11 kW – ikke 22 kW, da bilens onboard-lader er grænsen ved vekselstrøm. En 58 kWh-batteripakke lades fra næsten tom til fuld på ca. 5–6 timer ved 11 kW. På 1-faset (op til ~3,7 kW) tager det længere.',
    },
    {
      question: 'Hvilken ladeboks passer til en VW ID.3?',
      answer: 'Alle ladebokse med Type 2-stik passer til ID.3 – det er standarden. NexBlue Edge 2, Zaptec Go og Zaptec Go 2 fungerer alle perfekt. Selvom de kan levere 22 kW, lader ID.3 med op til 11 kW pga. bilens onboard-lader. Du får stadig fuld glæde af Saving Mode og laderabat.',
    },
    {
      question: 'Hvad koster det at lade en VW ID.3?',
      answer: 'Det afhænger af spotprisen og dit forbrug. ID.3 bruger typisk ~0,15–0,17 kWh/km (WLTP). Med enkel energi betaler du spotpris uden tillæg, får 10 øre/kWh i laderabat og kan med Saving Mode lade i de billigste timer. Elafgiften på hjemmeopladningen kan desuden refunderes automatisk via serviceaftalen.',
    },
    {
      question: 'Hvor langt kører en VW ID.3 på en opladning?',
      answer: 'Det afhænger af batteristørrelse og kørsel. Med ~58 kWh batteri er WLTP-rækkevidden typisk omkring 400+ km, og med den større ~77 kWh pakke endnu længere. Reel rækkevidde er lavere om vinteren og ved motorvejskørsel.',
    },
    {
      question: 'Kan jeg lynlade en VW ID.3?',
      answer: 'Ja. ID.3 understøtter DC-lynladning (CCS) med høj effekt afhængigt af model og version, så du kan lade en stor del af batteriet på under en halv time ved en hurtiglader. Til daglig er hjemmeopladning dog både billigst og nemmest.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'specs', label: 'ID.3 ladedata' },
    { id: 'ladeboks', label: 'Vælg ladeboks' },
    { id: 'billigt', label: 'Lad billigt' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Elbil', path: '/elbil' }, { name: 'Opladning VW ID.3', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Opladning af VW ID.3" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Guide · Elbil-opladning · 2026"
        h1="Opladning af VW ID.3: ladeboks, hastighed og pris"
        intro="Alt om at lade din Volkswagen ID.3 hjemme – ladehastighed, hvilken ladeboks der passer, og hvordan du gør hver opladning billigst muligt."
        badges={['Op til 11 kW AC', 'Type 2-stik', 'Saving Mode inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1617704548623-340376564e68?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '11 kW', label: 'AC onboard-lader' },
          { value: '~58/77 kWh', label: 'batteri (Pro / Pro S)' },
          { value: '~0,16 kWh/km', label: 'typisk forbrug (WLTP)' },
          { value: '10 øre/kWh', label: 'laderabat hos enkel' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Opladning af VW ID.3 – kort svar">
        <AnswerBox question="Hvilken ladeboks og hastighed passer til ID.3?">
          VW ID.3 lader hjemme med op til <strong>11 kW</strong> (bilens onboard AC-lader er grænsen). Enhver <strong>Type 2-ladeboks</strong>
          passer – NexBlue Edge 2, Zaptec Go og Zaptec Go 2 fungerer alle. En ~58 kWh-pakke lades fra næsten tom til fuld på ca. <strong>5–6
          timer</strong> ved 11 kW. Med Saving Mode sker det automatisk i de billigste timer.
        </AnswerBox>
      </ProseSection>

      <ProseSection id="specs" heading="VW ID.3 ladedata" alt>
        <div className="my-2">
          <ComparisonTable
            caption="VW ID.3 ladespecifikationer"
            columns={[{ name: 'VW ID.3' }]}
            rows={[
              { label: 'AC-opladning (hjem)', values: ['Op til 11 kW'] },
              { label: 'Stiktype (AC)', values: ['Type 2'] },
              { label: 'DC-lynladning', values: ['CCS, høj effekt'] },
              { label: 'Batteri (typisk)', values: ['~58 kWh / ~77 kWh'] },
              { label: 'Forbrug (WLTP)', values: ['~0,15–0,17 kWh/km'] },
              { label: 'Fuld hjemmeladning (58 kWh)', values: ['~5–6 timer ved 11 kW'] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">Kilde: Volkswagen officielle ID.3-specifikationer. Reelle tal varierer med version, temperatur og kørsel.</p>
      </ProseSection>

      <div id="ladeboks" className="container mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold text-center mb-3 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Bedste ladeboks til VW ID.3</h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-8 text-sm">Alle tre passer perfekt til ID.3 via Type 2. ID.3 lader med op til 11 kW – så du behøver ikke betale for mere effekt, men fremtidssikring kan stadig være værd at overveje.</p>
        <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="Bedst til prisen" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go} note="Mest pålidelig" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="V2G-ready" />
        </div>
      </div>

      <ProseSection id="billigt" heading="Sådan lader du din ID.3 billigst" alt>
        <StepList steps={[
          { title: 'Vælg spotpris uden tillæg', body: 'Hos enkel betaler du markedsprisen 1:1 fra 18 kr./md. uden binding.' },
          { title: 'Lad med Saving Mode', body: 'Appen flytter automatisk opladningen til de billigste timer på Nord Pool.' },
          { title: 'Få laderabat', body: '10 øre/kWh trækkes automatisk fra, når du lader via enkel-appen.' },
          { title: 'Få elafgift refunderet', body: 'Serviceaftalen (0 kr./md.) håndterer refusionen automatisk.' },
        ]} />
        <FactGrid columns={3} facts={[
          { value: 'Fra 18 kr./md.', label: 'Spotpris uden tillæg', sub: 'Ingen binding' },
          { value: 'Op til ~30%', label: 'Saving Mode-besparelse*', sub: 'Historisk 15–30%' },
          { value: '10 øre/kWh', label: 'Laderabat', sub: 'Via Saving Mode' },
        ]} />
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/opladere', label: 'Se ladebokse' },
        { path: '/elbil', label: 'Elbil-løsning hos enkel' },
        { path: '/portal/smart-charge', label: 'Sådan virker Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat – 10 øre/kWh' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks skal jeg vælge?' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
      ]} />

      <RichCTA
        headline="Lad din VW ID.3 billigere"
        subtext="Spotpris uden tillæg, Saving Mode og 10 øre laderabat – inkluderet til 0 kr./md."
        primary={{ path: '/elbil', text: 'Se elbil-løsning' }}
        secondary={{ path: '/opladere', text: 'Se ladebokse' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}